'use client';

import Button from '@/components/Button';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import { setSession } from '@/utils/tools';
import { ToLeft, ToRight } from '@zeelool/icon';
import Image from '@/components/Image';
import { useCallback, useState } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { Autoplay, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import ActivityProductList from '../../components/ActivityProductSwiper'; // Added import for ActivityProductList
import { useTransitionTracker } from '../hooks/useTransitionTracker';

interface LensPartProps {
  lensPartData: {
    bg_image: string;
    title_image: string;
    title: string;
    description: string;
    alt: string;
    color_title?: string;
    feature_arr: Array<{
      image: string;
      word: string;
    }>;
    color_arr: Array<{
      image: string;
      title: string;
      swiper_img: string;
    }>;
    infor_title?: string;
    btn_word: string;
    group_id: number;
    mk_name?: string;
    lists: Array<{
      id: number;
      shop_sku: string;
      business_sku: string;
      sku: string;
      name: string;
      short_name: string;
      image: {
        product: string[];
      };
      price: number;
      sell_price: number;
    }>;
    product_list?: Array<{
      id: number;
      shop_sku: string;
      business_sku: string;
      sku: string;
      name: string;
      short_name: string;
      image: {
        product: string[];
      };
      price: number;
      sell_price: number;
    }>;
    product_title?: string;
  };
}

// 添加replaceIcons函数
const replaceIcons = (content: string) => {
  try {
    if (typeof content !== 'string') return content;

    return content
      .replace(/<i class="iconfont icon-shangbiao"><\/i>/g, '®')
      .replace(/<i class="iconfont icon-line-trademarkshangbiao"><\/i>/g, '™');
  } catch {
    return content;
  }
};

const LensPart = ({ lensPartData }: LensPartProps) => {
  const { navigate } = useAppNavigation();
  const [chooseIndex, setChooseIndex] = useState(0);
  const [swiper, setSwiper] = useState<SwiperType | null>(null);
  const {
    trackColorBtnClick,
    trackFlipBtnClick,
    trackSwiperClick,
    trackProductExp,
    trackProductClick,
    trackViewMoreClick,
  } = useTransitionTracker();

  const handleSlideChange = useCallback(() => {
    if (swiper) {
      const { realIndex } = swiper;
      setChooseIndex(realIndex);
    }
  }, [swiper]);

  const handleColorClick = useCallback(
    (index: number) => {
      if (swiper) {
        swiper.slideToLoop(index);
        setChooseIndex(index);

        // 颜色按钮点击埋点
        trackColorBtnClick({
          group_id: `${lensPartData.group_id}`,
          mkt_name: lensPartData.mk_name,
          color_name: lensPartData.color_arr[index].title,
        });
      }
    },
    [swiper, lensPartData, trackColorBtnClick],
  );

  const handleViewMore = useCallback(
    (item?: { group_id?: number }) => {
      setSession('zl_goodsListEnterPageScene', '活动专题页');
      navigate(`/goods-list/${item?.group_id || lensPartData.group_id}`);
    },
    [navigate, lensPartData.group_id],
  );

  // 处理前进按钮点击事件
  const handleNextButtonClick = useCallback(() => {
    trackFlipBtnClick({
      group_id: `${lensPartData.group_id}`,
      mkt_name: lensPartData.mk_name,
      btn_name: 'right',
    });
  }, [lensPartData, trackFlipBtnClick]);

  // 处理后退按钮点击事件
  const handlePrevButtonClick = useCallback(() => {
    trackFlipBtnClick({
      group_id: `${lensPartData.group_id}`,
      mkt_name: lensPartData.mk_name,
      btn_name: 'left',
    });
  }, [lensPartData, trackFlipBtnClick]);

  // 轮播图点击
  const handleSwiperClick = useCallback(
    (index: number) => {
      trackSwiperClick({
        group_id: `${lensPartData.group_id}`,
        mkt_name: lensPartData.mk_name,
        image_index: index,
      });
      handleViewMore();
    },
    [lensPartData, trackSwiperClick, handleViewMore],
  );

  // 商品点击埋点
  const handleProductClick = useCallback(
    (params: { product: any; index: number }) => {
      trackProductClick({
        pits_index: params.index,
        group_id: `${lensPartData.group_id}`,
        mkt_name: lensPartData.mk_name,
        shop_sku: params.product.shop_sku,
        business_sku: params.product.business_sku,
        product_sku: params.product.sku,
        item: params.product,
      });
    },
    [lensPartData, trackProductClick],
  );

  // 商品曝光埋点
  const handleProductExp = useCallback(
    (params: { product: any; index: number }) => {
      trackProductExp({
        pits_index: params.index,
        group_id: `${lensPartData.group_id}`,
        mkt_name: lensPartData.mk_name,
        shop_sku: params.product.shop_sku,
        business_sku: params.product.business_sku,
        product_sku: params.product.sku,
        item: params.product,
      });
    },
    [lensPartData, trackProductExp],
  );

  // 查看更多点击埋点
  const handleViewMoreClick = useCallback(() => {
    trackViewMoreClick({
      group_id: `${lensPartData.group_id}`,
      mkt_name: lensPartData.mk_name,
    });
    handleViewMore();
  }, [lensPartData, trackViewMoreClick, handleViewMore]);

  return (
    <>
      <section className="w-full">
        <WebOnly>
          <div className="w-full bg-white">
            {/* 顶部区域 */}
            <div
              className="relative w-full bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: `url(${lensPartData.bg_image})` }}
            >
              <div className="flex items-center justify-between px-[60px] lg:px-[80px] 2xl:px-[120px] h-[31.25vw] min-h-[420px]">
                {/* 左侧描述区域 */}
                <div className="flex-1">
                  <div className="flex items-center mb-[8px] lg:mb-[10px] 2xl:mb-[13px]">
                    <Image
                      src={lensPartData.title_image}
                      alt={lensPartData.title}
                      width={335}
                      height={335}
                      className="w-[200px] lg:w-[280px] 2xl:w-[335px] h-auto mr-[20px] lg:mr-[30px] 2xl:mr-[40px]"
                      priority
                    />
                    <div>
                      <p className="text-[20px] leading-[22px] lg:text-[24px] lg:leading-[25px] 2xl:text-[36px] 2xl:leading-[37px] font-bb text-white mb-[6px] lg:mb-[8px] 2xl:mb-[10px]">
                        {lensPartData.title}
                      </p>
                      <p className="text-[16px] leading-[17px] lg:text-[18px] lg:leading-[20px] 2xl:text-[24px] 2xl:leading-[25px] text-white">
                        {lensPartData.description}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-end">
                    {/* 特性数组 */}
                    <ul className="flex flex-wrap w-[25.7vw]">
                      {lensPartData.feature_arr?.map((item, index) => (
                        <li
                          key={index}
                          className="flex items-center lg:w-[12vw] mt-[15px] lg:mt-[20px] 2xl:mt-[28px]"
                        >
                          <div className="w-[3.5vw] h-[3.5vw] relative">
                            <Image
                              src={item.image}
                              alt={item.word}
                              fill
                              className="object-contain"
                            />
                          </div>
                          <p className="flex-1 ml-[8px] lg:ml-[10px] 2xl:ml-[14px] text-[10px] lg:text-[11px] 2xl:text-[14px] leading-[16px] lg:leading-[18px] 2xl:leading-[20px] text-white text-ellipsis line-clamp-3">
                            {item.word}
                          </p>
                        </li>
                      ))}
                    </ul>

                    {/* 颜色选择 */}
                    {lensPartData.color_arr && (
                      <div className="ml-[2vw] flex-1 h-[fit-content]">
                        {lensPartData.color_title && (
                          <p className="text-[12px] lg:text-[13px] 2xl:text-[14px] text-white mb-[8px] lg:mb-[10px] 2xl:mb-[12px]">
                            {lensPartData.color_title}
                          </p>
                        )}
                        <ul className="flex flex-wrap">
                          {lensPartData.color_arr.map((item, index) => (
                            <li
                              key={index}
                              className={`mr-[2px] mt-[2px] border border-transparent p-[2px] cursor-pointer ${
                                chooseIndex === index ? 'border-white' : ''
                              }`}
                              onClick={() => handleColorClick(index)}
                            >
                              <div className="flex flex-col items-center w-[90px] lg:w-[100px] 2xl:w-[110px] min-w-[90px] lg:min-w-[96px] py-[6px] lg:py-[7px] 2xl:py-[8px] pb-[2px] lg:pb-[2px] 2xl:pb-[3px] bg-white">
                                <div className="w-[60px] lg:w-[70px] 2xl:w-[77px] relative">
                                  <Image
                                    src={item.image}
                                    alt={item.title}
                                    width={77}
                                    height={30}
                                    className="w-full h-auto object-contain"
                                  />
                                </div>
                                <p className="mt-[2px] text-[12px] lg:text-[13px] 2xl:text-[14px] min-text-[11px]">
                                  {item.title}
                                </p>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                {/* 右侧轮播区域 */}
                <div className="w-[38vw] h-[31.25vw] min-h-[420px]">
                  {lensPartData.color_arr && lensPartData.color_arr.length > 1 ? (
                    <div className="relative h-full">
                      <Swiper
                        onSwiper={setSwiper}
                        modules={[Navigation, Autoplay]}
                        slidesPerView={1}
                        slidesPerGroup={1}
                        loop
                        observer
                        observeParents
                        observeSlideChildren
                        watchSlidesProgress
                        speed={300}
                        autoplay={{
                          delay: 5000,
                          disableOnInteraction: false,
                          pauseOnMouseEnter: true,
                        }}
                        navigation={{
                          nextEl: '.pc-lens-next',
                          prevEl: '.pc-lens-prev',
                        }}
                        onSlideChange={handleSlideChange}
                        className="h-full cursor-pointer"
                      >
                        {lensPartData.color_arr.map((item, index) => (
                          <SwiperSlide
                            key={index}
                            className="h-full"
                            onClick={() => handleSwiperClick(index)}
                          >
                            <Image
                              src={item.swiper_img}
                              alt={`${lensPartData.alt}${index}`}
                              width={728}
                              height={600}
                              className="w-full h-full object-cover"
                              priority
                            />
                          </SwiperSlide>
                        ))}
                      </Swiper>
                      {/* 导航按钮 */}
                      <button
                        type="button"
                        className="pc-lens-prev absolute left-0 top-1/2 -translate-y-1/2 z-[1] w-[40px] h-[40px] lg:w-[48px] lg:h-[48px] 2xl:w-[56px] 2xl:h-[56px] bg-black flex items-center justify-center cursor-pointer"
                        onClick={handlePrevButtonClick}
                      >
                        <ToLeft className="text-[20px] lg:text-[24px] 2xl:text-[30px] text-white" />
                      </button>
                      <button
                        type="button"
                        className="pc-lens-next absolute right-0 top-1/2 -translate-y-1/2 z-[1] w-[40px] h-[40px] lg:w-[48px] lg:h-[48px] 2xl:w-[56px] 2xl:h-[56px] bg-black flex items-center justify-center cursor-pointer"
                        onClick={handleNextButtonClick}
                      >
                        <ToRight className="text-[20px] lg:text-[24px] 2xl:text-[30px] text-white" />
                      </button>
                    </div>
                  ) : (
                    lensPartData.color_arr?.[0] && (
                      <div
                        className="w-full h-full cursor-pointer"
                        onClick={() => handleViewMore()}
                      />
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
        </WebOnly>
        <WapOnly>
          <div className="w-full bg-white">
            {/* 顶部区域 */}
            <div
              className="relative w-full h-[640px] px-[20px] bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: `url(${lensPartData.bg_image})` }}
            >
              {/* 描述区域 */}
              <div className="flex items-center h-[106px] mt-[3px]">
                <div className="flex items-center">
                  <Image
                    src={lensPartData.title_image}
                    alt={lensPartData.title}
                    width={160}
                    height={160}
                    className="w-[160px] h-auto mr-[15px]"
                  />
                  <div>
                    <p className="text-[16px] leading-[16px] font-be text-white mb-[6px]">
                      {lensPartData.title}
                    </p>
                    <p className="text-[12px] leading-[13px] font-br text-white/90">
                      {lensPartData.description}
                    </p>
                  </div>
                </div>
              </div>

              {/* 轮播图 */}
              {lensPartData.color_arr && lensPartData.color_arr.length > 1 ? (
                <div className="relative">
                  <div className="w-full h-[276px]">
                    <Swiper
                      onSwiper={setSwiper}
                      modules={[Navigation, Autoplay]}
                      slidesPerView={1}
                      slidesPerGroup={1}
                      loop
                      // loopAdditionalSlides={2}
                      observer
                      observeParents
                      observeSlideChildren
                      watchSlidesProgress
                      speed={300}
                      autoplay={{
                        delay: 5000,
                        disableOnInteraction: false,
                        pauseOnMouseEnter: true,
                      }}
                      navigation={{
                        nextEl: '.mobile-lens-next',
                        prevEl: '.mobile-lens-prev',
                      }}
                      onSlideChange={handleSlideChange}
                      className="h-full"
                    >
                      {lensPartData.color_arr.map((item, index) => (
                        <SwiperSlide
                          key={index}
                          className="h-full cursor-pointer"
                          onClick={() => handleSwiperClick(index)}
                        >
                          <Image
                            src={item.swiper_img}
                            alt={`${lensPartData.alt}${index}`}
                            width={300}
                            height={276}
                            className="w-full h-full object-cover"
                            priority
                          />
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  </div>

                  {/* 导航按钮 */}
                  <button
                    className="mobile-lens-prev absolute left-0 bottom-[128px] z-[1] w-[28px] h-[28px] bg-black flex items-center justify-center"
                    onClick={handlePrevButtonClick}
                  >
                    <ToLeft className="text-[15px] text-white" />
                  </button>
                  <button
                    className="mobile-lens-next absolute right-0 bottom-[128px] z-[1] w-[28px] h-[28px] bg-black flex items-center justify-center"
                    onClick={handleNextButtonClick}
                  >
                    <ToRight className="text-[15px] text-white" />
                  </button>
                </div>
              ) : (
                lensPartData.color_arr?.[0] && (
                  <div className="cursor-pointer" onClick={() => handleViewMore()}>
                    <Image
                      src={lensPartData.color_arr[0].swiper_img}
                      alt={`${lensPartData.alt}0`}
                      width={300}
                      height={276}
                      className="w-full h-[276px] object-cover"
                      priority
                    />
                  </div>
                )
              )}

              {/* 颜色选择 */}
              {lensPartData.color_arr && (
                <div className="w-full my-[10px]">
                  <ul className="flex justify-center flex-wrap">
                    {lensPartData.color_arr.map((item, index) => (
                      <li
                        key={index}
                        className={`w-[45px] h-[45px] flex items-center justify-center mr-[2px] mt-[2px] border border-transparent rounded-full box-border ${
                          chooseIndex === index ? 'border-white' : ''
                        }`}
                        onClick={() => handleColorClick(index)}
                      >
                        <Image
                          src={item.image}
                          alt={item.title}
                          width={39}
                          height={39}
                          className="w-[39px] h-[39px]"
                        />
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* 特性列表 */}
              <ul className="flex flex-wrap justify-between">
                {lensPartData.feature_arr?.map((item, index) => (
                  <li key={index} className="flex items-center w-1/2 h-[45px] mt-[4px] text-white">
                    <Image
                      src={item.image}
                      alt={item.word}
                      width={34}
                      height={34}
                      className="w-[34px] h-auto"
                    />
                    <p className="ml-[7px] text-[12px] leading-[14px] line-clamp-3">{item.word}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </WapOnly>
      </section>

      {/* 商品列表 */}
      {lensPartData.product_list && lensPartData.product_list.length > 0 ? (
        <div className="w-full bg-white pt-[20px] pb-[40px] xl:py-[40px] 2xl:py-[70px]">
          {/* 标题区域 */}
          <div className="title px-[24px] lg:px-[80px] 2xl:px-[120px]">
            <WebOnly>
              <div className="flex items-center justify-between pb-[20px] xl:pb-[30px] 2xl:pb-[45px]">
                <div className="flex-1 mr-[20px]">
                  <h2
                    className="text-[20px] xl:text-[28px] 2xl:text-[32px] font-bb"
                    dangerouslySetInnerHTML={{
                      __html: replaceIcons(lensPartData.infor_title || ''),
                    }}
                  />
                </div>
                <div>
                  <Button
                    variant="outlined"
                    onClick={handleViewMoreClick}
                    className="!font-bb !text-[#282828]"
                    sx={{
                      width: { xs: '190px', xl: '230px' },
                      height: { xs: '40px', xl: '60px' },
                      fontSize: { xs: '14px', xl: '24px' },
                      border: '2px solid #000',
                      borderRadius: '10px',
                      '&:hover': {
                        backgroundColor: '#ffffff',
                      },
                    }}
                  >
                    {lensPartData.btn_word}
                  </Button>
                </div>
              </div>
            </WebOnly>
            <WapOnly>
              <h2
                className="desc w-[290px] mx-auto text-center text-[14px] font-bb leading-[18px] text-ellipsis line-clamp-3 mb-[20px]"
                dangerouslySetInnerHTML={{ __html: replaceIcons(lensPartData.infor_title || '') }}
              />
            </WapOnly>
          </div>

          {/* 商品列表 */}
          <ActivityProductList
            products={lensPartData.product_list}
            moduleId={lensPartData.group_id}
            onTrack={handleProductClick}
            onExpTrack={handleProductExp}
            cardPadding="sm:!px-[24px] lg:!px-[80px] 2xl:!px-[120px]"
          />

          {/* 移动端View More按钮 */}
          <WapOnly>
            <Button
              variant="contained"
              onClick={handleViewMoreClick}
              className="!font-bb"
              sx={{
                width: '250px',
                height: '40px',
                fontSize: '12px',
                borderRadius: '20px',
                margin: '28px auto 0',
                display: 'block',
              }}
            >
              VIEW MORE
            </Button>
          </WapOnly>
        </div>
      ) : (
        <div className="text-center py-4 text-gray-500" />
      )}
    </>
  );
};

export default LensPart;
